// SafeHeir — lien de transmission, côté bénéficiaire (PF-03 CP4b-1 et CP4b-2, DEC-039).
// Aucun compte ni MK : le jeton lu dans le fragment de l'URL (K128) est la seule
// autorisation. Il reste en mémoire, jamais dans le stockage du navigateur
// (RULES §1.6), et n'est envoyé qu'aux Edge Functions verify-transmission-link et
// submit-transmission-documents.

import { supabase } from './supabaseClient';

// Jeton émis par issue-transmission-link : 32 octets en base64url sans padding.
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/**
 * Extrait le jeton du fragment de l'URL.
 * @param {string} hash window.location.hash (le « # » initial est facultatif)
 * @returns {string | null} jeton de 43 caractères base64url, ou null
 */
export const readLinkToken = (hash) => {
  const candidate = typeof hash === 'string' ? hash.replace(/^#/, '') : '';
  return TOKEN_PATTERN.test(candidate) ? candidate : null;
};

// 10 Mio par fichier : même plafond que submit-transmission-documents et le bucket.
const MAX_FILE_BYTES = 10 * 1024 * 1024;

// Types acceptés. Le serveur reconnaît le type par les premiers octets ; ce
// contrôle évite seulement un envoi voué au refus. Type déclaré vide (certains
// systèmes) : repli sur l'extension du nom.
const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const ACCEPTED_EXTENSION = /\.(pdf|jpe?g|png)$/i;

// Code d'erreur stable du corps JSON ; undefined si absent ou illisible.
const readErrorCode = async (response) => {
  try {
    const body = await response.json();
    return typeof body?.code === 'string' ? body.code : undefined;
  } catch {
    return undefined;
  }
};

// Retry-After (secondes, exposé par la fonction) arrondi à la minute
// supérieure, 1 au minimum, y compris si l'en-tête est absent ou illisible.
const readRetryAfterMinutes = (response) => {
  const seconds = Number(response?.headers?.get?.('Retry-After'));
  return Number.isFinite(seconds) ? Math.max(1, Math.ceil(seconds / 60)) : 1;
};

// Refus communs aux deux fonctions (vérification du lien et limitation de
// débit, code serveur partagé). Seul le code renvoyé par la fonction fait foi :
// une erreur de passerelle (fonction absente, 5xx) ou de réseau n'en porte pas
// et reste une erreur.
const linkRefusal = (code, response) => {
  switch (code) {
    case 'invalid_payload':
    case 'link_not_found':
      return { outcome: 'invalid' };
    case 'not_eligible':
      return { outcome: 'not_eligible' };
    case 'link_expired':
    case 'link_invalidated':
    case 'link_consumed':
      return { outcome: 'gone' };
    case 'rate_limited':
      return { outcome: 'rate_limited', retryAfterMinutes: readRetryAfterMinutes(response) };
    case 'service_unavailable':
      return { outcome: 'unavailable' };
    default:
      return { outcome: 'error' };
  }
};

const isAcceptedFile = (file) =>
  ACCEPTED_TYPES.includes(file.type) || (file.type === '' && ACCEPTED_EXTENSION.test(file.name));

/**
 * Vérifie le lien auprès de verify-transmission-link. Ne lève jamais :
 * chaque réponse devient un résultat.
 * @param {string} token
 * @returns {Promise<
 *   { outcome: 'valid', ownerFirstName: string | null, status: string,
 *     expiresAt: string, submittedAt: string | null }
 *   | { outcome: 'invalid' }
 *   | { outcome: 'gone' }
 *   | { outcome: 'not_eligible' }
 *   | { outcome: 'rate_limited', retryAfterMinutes: number }
 *   | { outcome: 'unavailable' }
 *   | { outcome: 'error' }
 * >}
 */
export const verifyTransmissionLink = async (token) => {
  if (!supabase) return { outcome: 'error' };

  const { data, error } = await supabase.functions.invoke('verify-transmission-link', {
    body: { token },
  });

  if (!error) {
    return data?.success
      ? {
          outcome:        'valid',
          ownerFirstName: data.owner_first_name ?? null,
          status:         data.status,
          expiresAt:      data.expires_at,
          submittedAt:    data.submitted_at ?? null,
        }
      : { outcome: 'error' };
  }

  // Réponse non 2xx : error.context est la Response, corps non lu.
  return linkRefusal(await readErrorCode(error.context), error.context);
};

/**
 * Dépose les deux pièces auprès de submit-transmission-documents. Ne lève
 * jamais. Contrôles locaux avant tout appel réseau ; le FormData part tel quel,
 * le navigateur pose Content-Type (frontière multipart) et Content-Length.
 * @param {string} token
 * @param {{ deathCertificate: File | null, identityDocument: File | null }} files
 * @returns {Promise<
 *   { outcome: 'submitted', submittedAt: string }
 *   | { outcome: 'missing_file' | 'empty_file' | 'file_too_large' | 'payload_too_large'
 *       | 'unsupported_file_type' | 'already_submitted' | 'invalid' | 'gone'
 *       | 'not_eligible' | 'unavailable' | 'error' }
 *   | { outcome: 'rate_limited', retryAfterMinutes: number }
 * >}
 */
export const submitTransmissionDocuments = async (token, { deathCertificate, identityDocument }) => {
  if (!deathCertificate || !identityDocument) return { outcome: 'missing_file' };
  const files = [deathCertificate, identityDocument];
  if (files.some((file) => file.size === 0)) return { outcome: 'empty_file' };
  if (files.some((file) => file.size > MAX_FILE_BYTES)) return { outcome: 'file_too_large' };
  if (!files.every(isAcceptedFile)) return { outcome: 'unsupported_file_type' };
  if (!supabase) return { outcome: 'error' };

  const form = new FormData();
  form.append('token', token);
  form.append('death_certificate', deathCertificate);
  form.append('identity_document', identityDocument);

  const { data, error } = await supabase.functions.invoke('submit-transmission-documents', {
    body: form,
  });

  if (!error) {
    return data?.success && typeof data.submitted_at === 'string'
      ? { outcome: 'submitted', submittedAt: data.submitted_at }
      : { outcome: 'error' };
  }

  // invalid_payload : le jeton a été validé à l'ouverture, ce code ne peut
  // signifier ici qu'un envoi incomplet ou abîmé. Erreur d'envoi, pas refus du
  // lien (verifyTransmissionLink garde invalid_payload -> invalid).
  const code = await readErrorCode(error.context);
  switch (code) {
    case 'invalid_payload':
      return { outcome: 'error' };
    case 'file_too_large':
    case 'payload_too_large':
    case 'unsupported_file_type':
    case 'already_submitted':
      return { outcome: code };
    default:
      return linkRefusal(code, error.context);
  }
};
